/**
 * Normal computation configuration and per-face shading override.
 *
 * Bulk normal computation lives in the WASM module (wasm/normals.ts). This
 * file contains the resolution helpers and the JS-side per-face shading
 * post-process that overrides selected faces after the bulk pass.
 */

import type { LoopNormals } from "../normalTypes.ts";
import type {
  FaceAttributeTable,
  PolygonTopology,
  ShadingMode,
} from "../types.ts";
import {
  FACE_SHADING_FLAT,
  FACE_SHADING_INHERIT,
  FACE_SHADING_SMOOTH,
} from "../types.ts";

/** Default auto-smooth angle in degrees (matches Blender 4.x). */
export const AUTO_SMOOTH_ANGLE_DEFAULT = 30;

/**
 * Resolve the effective auto-smooth angle from a SceneObject's shading
 * properties:
 *  - `"flat"` → 0° (all edges sharp)
 *  - `"smooth"` → 180° (all edges averaged)
 *  - `"autoSmooth"` → `autoSmoothAngle ?? 30°` (threshold-based)
 */
export function resolveAutoSmoothAngle(
  shading: ShadingMode,
  autoSmoothAngle?: number,
): number {
  if (shading === "flat") return 0;
  if (shading === "smooth") return 180;
  return autoSmoothAngle ?? AUTO_SMOOTH_ANGLE_DEFAULT;
}

/**
 * Apply per-face shading overrides from `faceAttributes.shading` on top of
 * an already-computed `loopNormals` array. Mutates `loopNormals` in place.
 *
 * Semantic (Blender-equivalent for the per-face case):
 *  - `INHERIT` (0): leave the face's corners untouched (the bulk auto-smooth
 *    result governs).
 *  - `FLAT` (1): replace every corner of the face with the face's geometric
 *    normal, rendering a hard shading on every edge of this face from this
 *    face's side. Adjacent faces are unaffected (their corners still average
 *    in this face's contribution).
 *  - `SMOOTH` (2): replace every corner with the angle-weighted vertex
 *    normal, ignoring the auto-smooth threshold and any sharp / seam flags.
 *    Equivalent to "fully smooth this face regardless of object setting".
 *  - `AUTO` (3): treated as INHERIT — the bulk pass already used the
 *    object-wide auto-smooth angle. (Per-face auto-smooth angle is reserved
 *    for a future Rust-side extension.)
 *
 * Returns the (possibly same) `LoopNormals` instance for chaining. No-op
 * when `faceAttributes` carries no shading data.
 */
export function applyFaceShadingOverride(
  loopNormals: LoopNormals,
  vertices: Float32Array,
  topology: PolygonTopology,
  faceAttributes: FaceAttributeTable | undefined,
): LoopNormals {
  const shading = faceAttributes?.shading;
  if (!shading || shading.length === 0) return loopNormals;

  const { offsets, corners } = topology;
  const faceCount = offsets.length - 1;
  const limit = Math.min(shading.length, faceCount);

  // Lazily compute structures only the SMOOTH override needs.
  let vertexNormals: Float32Array | null = null;

  for (let f = 0; f < limit; f++) {
    const mode = shading[f];
    if (mode === FACE_SHADING_INHERIT) continue;

    const fs = offsets[f];
    const fe = offsets[f + 1];
    const faceSize = fe - fs;
    if (faceSize < 3) continue;

    if (mode === FACE_SHADING_FLAT) {
      // Newell's method — accurate face normal for any planar / near-planar
      // n-gon. Avoids the degeneracy of a single triangle pick on highly
      // non-convex faces.
      let nx = 0;
      let ny = 0;
      let nz = 0;
      for (let c = fs; c < fe; c++) {
        const a = corners[c];
        const b = corners[c + 1 < fe ? c + 1 : fs];
        const ax = vertices[a * 3];
        const ay = vertices[a * 3 + 1];
        const az = vertices[a * 3 + 2];
        const bx = vertices[b * 3];
        const by = vertices[b * 3 + 1];
        const bz = vertices[b * 3 + 2];
        nx += (ay - by) * (az + bz);
        ny += (az - bz) * (ax + bx);
        nz += (ax - bx) * (ay + by);
      }
      const len = Math.hypot(nx, ny, nz);
      if (len > 0) {
        nx /= len;
        ny /= len;
        nz /= len;
      } else {
        nx = 0;
        ny = 0;
        nz = 1;
      }
      for (let c = fs; c < fe; c++) {
        loopNormals[c * 3] = nx;
        loopNormals[c * 3 + 1] = ny;
        loopNormals[c * 3 + 2] = nz;
      }
      continue;
    }

    if (mode === FACE_SHADING_SMOOTH) {
      // SMOOTH override needs per-vertex normals. Build them on first hit
      // (angle-weighted accumulation across every face — no sharp logic),
      // then reuse for subsequent SMOOTH faces.
      if (!vertexNormals) {
        vertexNormals = computeAngleWeightedVertexNormals(vertices, topology);
      }
      for (let c = fs; c < fe; c++) {
        const v = corners[c];
        loopNormals[c * 3] = vertexNormals[v * 3];
        loopNormals[c * 3 + 1] = vertexNormals[v * 3 + 1];
        loopNormals[c * 3 + 2] = vertexNormals[v * 3 + 2];
      }
    }
    // AUTO and any future / unknown values: leave as the bulk pass.
  }

  return loopNormals;
}

/**
 * Per-vertex normals computed by accumulating the corner-angle-weighted
 * face normal of every incident face. Sharp / seam flags are ignored —
 * this is the "fully smooth" reference used by `FACE_SHADING_SMOOTH`.
 */
function computeAngleWeightedVertexNormals(
  vertices: Float32Array,
  topology: PolygonTopology,
): Float32Array {
  const vCount = vertices.length / 3;
  const out = new Float32Array(vCount * 3);
  const { offsets, corners } = topology;
  const faceCount = offsets.length - 1;

  for (let f = 0; f < faceCount; f++) {
    const fs = offsets[f];
    const fe = offsets[f + 1];
    const size = fe - fs;
    if (size < 3) continue;

    // Face normal via Newell's method.
    let nx = 0;
    let ny = 0;
    let nz = 0;
    for (let c = fs; c < fe; c++) {
      const a = corners[c];
      const b = corners[c + 1 < fe ? c + 1 : fs];
      nx +=
        (vertices[a * 3 + 1] - vertices[b * 3 + 1]) *
        (vertices[a * 3 + 2] + vertices[b * 3 + 2]);
      ny +=
        (vertices[a * 3 + 2] - vertices[b * 3 + 2]) *
        (vertices[a * 3] + vertices[b * 3]);
      nz +=
        (vertices[a * 3] - vertices[b * 3]) *
        (vertices[a * 3 + 1] + vertices[b * 3 + 1]);
    }
    const flen = Math.hypot(nx, ny, nz);
    if (flen === 0) continue;
    nx /= flen;
    ny /= flen;
    nz /= flen;

    // Accumulate angle-weighted face normal at each corner of this face.
    for (let c = fs; c < fe; c++) {
      const prev = corners[c === fs ? fe - 1 : c - 1];
      const cur = corners[c];
      const next = corners[c === fe - 1 ? fs : c + 1];
      const pcx = vertices[prev * 3] - vertices[cur * 3];
      const pcy = vertices[prev * 3 + 1] - vertices[cur * 3 + 1];
      const pcz = vertices[prev * 3 + 2] - vertices[cur * 3 + 2];
      const ncx = vertices[next * 3] - vertices[cur * 3];
      const ncy = vertices[next * 3 + 1] - vertices[cur * 3 + 1];
      const ncz = vertices[next * 3 + 2] - vertices[cur * 3 + 2];
      const lp = Math.hypot(pcx, pcy, pcz);
      const ln = Math.hypot(ncx, ncy, ncz);
      const denom = lp * ln;
      let angle = 0;
      if (denom > 0) {
        let dot = (pcx * ncx + pcy * ncy + pcz * ncz) / denom;
        if (dot > 1) dot = 1;
        else if (dot < -1) dot = -1;
        angle = Math.acos(dot);
      }
      out[cur * 3] += nx * angle;
      out[cur * 3 + 1] += ny * angle;
      out[cur * 3 + 2] += nz * angle;
    }
  }

  // Normalize.
  for (let v = 0; v < vCount; v++) {
    const x = out[v * 3];
    const y = out[v * 3 + 1];
    const z = out[v * 3 + 2];
    const len = Math.hypot(x, y, z);
    if (len > 0) {
      out[v * 3] = x / len;
      out[v * 3 + 1] = y / len;
      out[v * 3 + 2] = z / len;
    } else {
      out[v * 3 + 2] = 1;
    }
  }

  return out;
}
